import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const FAQSection = () => {
  const [openFAQ, setOpenFAQ] = useState(0);

  const faqs = [
    {
      category: "Getting Started",
      questions: [
        {
          question: "How does ProConnect work for contractors?",
          answer: `ProConnect is Tampa Bay's exclusive premium home service marketplace. We connect licensed contractors with pre-screened, high-value customers who are ready to pay premium rates for quality work.\n\nHere's how it works:\n• We handle all marketing and customer acquisition\n• You receive qualified leads for jobs in your service area\n• Complete the work with guaranteed payment protection\n• Get paid within 24 hours of job completion\n• No marketing costs, no payment chasing, no tire-kickers`,
          videoUrl: "https://example.com/how-it-works"
        },
        {
          question: "What makes ProConnect different from other lead services?",
          answer: `Unlike traditional lead services that charge per lead and provide low-quality prospects, ProConnect offers:\n\n• Zero marketing costs - we don't charge for leads\n• Premium customers only - pre-screened and qualified\n• Payment protection - guaranteed payment within 24 hours\n• Higher job values - average $2,847 vs industry $1,200\n• Exclusive network - limited to 235 founding contractors\n• No competition on price - customers choose based on quality`,
          videoUrl: null
        },
        {
          question: "How much does it cost to join ProConnect?",
          answer: `There are absolutely no upfront costs, monthly fees, or lead charges for founding contractors. ProConnect makes money only when you do - through a small percentage of completed jobs.\n\nWhat you save:\n• No Google Ads expenses ($1,200-3,000/month)\n• No HomeAdvisor fees ($600-1,500/month)\n• No Facebook marketing costs ($500-1,200/month)\n• No administrative overhead for lead management\n\nMost contractors save $30,000-60,000 annually on marketing costs alone.`,
          videoUrl: null
        }
      ]
    },
    {
      category: "Requirements & Eligibility",
      questions: [
        {
          question: "What are the requirements to join ProConnect?",
          answer: `To maintain our premium marketplace standards, all contractors must meet these requirements:\n\n• Valid Florida state license in your trade\n• General liability insurance (minimum $1M coverage)\n• 3+ years of professional experience\n• Clean background check and license verification\n• Service area within Tampa Bay region\n• Commitment to quality workmanship and customer service\n\nWe verify all credentials before approval to ensure customer confidence.`,
          videoUrl: null
        },
        {
          question: "Which trades are accepted in the network?",
          answer: `Currently accepting licensed contractors in these trades:\n\n• HVAC Contractors (CAC license)\n• Licensed Plumbers (CFC license)\n• Licensed Electricians (EC license)\n• Multi-trade contractors with multiple licenses\n\nWe're expanding to additional trades based on customer demand. Priority is given to contractors with multiple certifications and specializations.`,
          videoUrl: null
        },
        {
          question: "Do I need to be available 24/7?",
          answer: `No, you set your own schedule and availability. ProConnect respects work-life balance:\n\n• Set your preferred working hours\n• Choose which days you're available\n• Specify emergency service availability (optional)\n• Block out vacation time or busy periods\n• Control your job volume and workload\n\nCustomers are matched with available contractors based on their scheduling preferences.`,
          videoUrl: null
        }
      ]
    },
    {
      category: "Earnings & Payments",
      questions: [
        {
          question: "How much can I realistically earn with ProConnect?",
          answer: `Based on our current contractor network data:\n\n• Average annual increase: $13,000-18,000\n• Top performers: $25,000+ additional revenue\n• Average job value: $2,847 (vs industry $1,200)\n• Monthly job volume: 18-24 jobs per contractor\n• Customer satisfaction rate: 96%\n\nEarnings depend on your trade, experience level, service area, and availability. Most contractors see significant increases within 3-6 months.`,
          videoUrl: "https://example.com/earnings-breakdown"
        },
        {
          question: "How does the 24-hour payment guarantee work?",
          answer: `ProConnect's payment protection ensures you get paid quickly and reliably:\n\n• Customer pays ProConnect upfront before work begins\n• Funds are held in escrow during job completion\n• You submit completion confirmation through our app\n• Payment is released to your account within 24 hours\n• No more chasing invoices or waiting 30-90 days\n• 100% payment guarantee - we cover any disputes\n\nThis dramatically improves cash flow and eliminates payment stress.`,
          videoUrl: null
        },
        {
          question: "What percentage does ProConnect take?",
          answer: `ProConnect takes a competitive service fee only from completed jobs - no upfront costs or monthly fees.\n\nThe exact percentage varies by:\n• Job type and complexity\n• Customer acquisition cost\n• Payment processing and insurance\n• Platform maintenance and support\n\nMost contractors find the fee is offset by:\n• Eliminated marketing expenses\n• Higher job values\n• Guaranteed payments\n• Reduced administrative work\n\nNet result: significant increase in take-home income.`,
          videoUrl: null
        }
      ]
    },
    {
      category: "Customer Quality & Jobs",
      questions: [
        {
          question: "How do you ensure customer quality?",
          answer: `ProConnect maintains strict customer standards through our screening process:\n\n• Financial verification before job posting\n• Background checks on all customers\n• Previous contractor ratings and reviews\n• Payment history verification\n• Property ownership or rental authorization\n• Realistic budget expectations for quality work\n\nWe reject customers who haggle on price, have payment issues, or unrealistic expectations.`,
          videoUrl: null
        },
        {
          question: "What types of jobs will I receive?",
          answer: `ProConnect focuses on higher-value, quality-focused jobs:\n\n• Residential service calls and repairs\n• System installations and replacements\n• Preventive maintenance contracts\n• Emergency services (optional)\n• Commercial small-to-medium projects\n• Insurance claim work\n\nAverage job values are 137% higher than industry standard because customers value quality over lowest price.`,
          videoUrl: null
        },
        {
          question: "Can I choose which jobs to accept?",
          answer: `Absolutely. You have complete control over your job selection:\n\n• Review job details before accepting\n• See customer ratings and history\n• View job location and requirements\n• Check scheduling compatibility\n• Decline jobs that don't fit your expertise\n• Set preferences for job types and sizes\n\nNo penalties for declining jobs - we want you working on projects where you can deliver your best work.`,
          videoUrl: null
        }
      ]
    }
  ];

  const handleFAQToggle = (categoryIndex, questionIndex) => {
    const faqIndex = faqs?.slice(0, categoryIndex)?.reduce((acc, cat) => acc + cat?.questions?.length, 0) + questionIndex;
    setOpenFAQ(openFAQ === faqIndex ? -1 : faqIndex);
  };

  const getFAQIndex = (categoryIndex, questionIndex) => {
    return faqs?.slice(0, categoryIndex)?.reduce((acc, cat) => acc + cat?.questions?.length, 0) + questionIndex;
  };

  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-6">
            Frequently Asked Questions
          </h2>
          <p className="text-xl text-muted-foreground">
            Get answers to common questions about joining ProConnect's exclusive contractor network.
          </p>
        </div>

        {/* FAQ Categories */}
        <div className="space-y-12">
          {faqs?.map((category, categoryIndex) => (
            <div key={categoryIndex}>
              {/* Category Header */}
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-foreground mb-2">
                  {category?.category}
                </h3>
                <div className="w-16 h-1 bg-primary rounded-full" />
              </div>

              {/* Questions in Category */}
              <div className="space-y-4">
                {category?.questions?.map((faq, questionIndex) => {
                  const faqIndex = getFAQIndex(categoryIndex, questionIndex);
                  const isOpen = openFAQ === faqIndex;

                  return (
                    <div
                      key={questionIndex}
                      className="bg-card border border-border rounded-lg shadow-testimonial overflow-hidden"
                    >
                      {/* Question Header */}
                      <button
                        onClick={() => handleFAQToggle(categoryIndex, questionIndex)}
                        className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-muted/50 transition-colors duration-200"
                      >
                        <h4 className="text-lg font-semibold text-foreground pr-4">
                          {faq?.question}
                        </h4>
                        <div className={`flex-shrink-0 transition-transform duration-200 ${
                          isOpen ? 'rotate-180' : ''
                        }`}>
                          <Icon name="ChevronDown" size={20} className="text-muted-foreground" />
                        </div>
                      </button>
                      {/* Answer Content */}
                      <div className={`transition-all duration-300 ease-in-out ${
                        isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                      } overflow-hidden`}>
                        <div className="px-6 pb-6">
                          <div className="border-t border-border pt-4">
                            {/* Answer Text */}
                            <div className="text-muted-foreground leading-relaxed whitespace-pre-line mb-4">
                              {faq?.answer}
                            </div>

                            {/* Video Link if Available */}
                            {faq?.videoUrl && (
                              <div className="flex items-center p-4 bg-primary/5 border border-primary/20 rounded-lg">
                                <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center mr-3">
                                  <Icon name="Play" size={20} color="white" />
                                </div>
                                <div>
                                  <div className="font-medium text-foreground">Watch Video Explanation</div>
                                  <div className="text-sm text-muted-foreground">
                                    See a detailed walkthrough of this topic
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Still Have Questions CTA */}
        <div className="mt-16 text-center">
          <div className="bg-muted/50 rounded-xl p-8 border border-border">
            <h3 className="text-2xl font-bold text-foreground mb-4">
              Still Have Questions?
            </h3>
            <p className="text-muted-foreground mb-6">
              Our contractor success team is here to help you understand how ProConnect 
              can transform your business.
            </p>
            
            <div className="grid sm:grid-cols-2 gap-6 max-w-2xl mx-auto">
              <div className="flex items-center p-4 bg-card border border-border rounded-lg">
                <div className="w-10 h-10 bg-secondary rounded-lg flex items-center justify-center mr-3">
                  <Icon name="Phone" size={20} color="white" />
                </div>
                <div className="text-left">
                  <div className="font-medium text-foreground">Call Us</div>
                  <div className="text-sm text-muted-foreground">(813) 555-0199</div>
                </div>
              </div>
              
              <div className="flex items-center p-4 bg-card border border-border rounded-lg">
                <div className="w-10 h-10 bg-accent rounded-lg flex items-center justify-center mr-3">
                  <Icon name="Mail" size={20} color="white" />
                </div>
                <div className="text-left">
                  <div className="font-medium text-foreground">Email Us</div>
                  <div className="text-sm text-muted-foreground">contractors@proconnect.com</div>
                </div>
              </div>
            </div>

            <div className="mt-6 text-sm text-muted-foreground">
              Response time: Within 2 hours during business hours
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="mt-12 grid sm:grid-cols-3 gap-8 text-center">
          <div>
            <div className="text-2xl font-bold text-primary mb-2">96%</div>
            <div className="text-sm text-muted-foreground">Contractor Satisfaction</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-accent mb-2">24 Hours</div>
            <div className="text-sm text-muted-foreground">Average Response Time</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-secondary mb-2">265+</div>
            <div className="text-sm text-muted-foreground">Active Contractors</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;